import React from 'react';
import { FinancePlatformSummary, PlatformModuleKey } from '../../shared/types';
import { usePlatformUsdRates } from '../../shared/hooks/usePlatformUsdRates';
import { formatCryptoAmount, formatUsd } from './format';

const MODULE_LABELS: Record<PlatformModuleKey, string> = {
  p2p: 'P2P',
  swap: 'Swap',
  withdrawal: 'Withdrawals',
  deposit: 'Deposits',
};

interface PlatformSummaryCardsProps {
  summary: FinancePlatformSummary | null;
  isLoading: boolean;
  /** When set, the fees card shows only this module; otherwise all modules. */
  moduleKey?: PlatformModuleKey;
}

/**
 * Platform-side view: collected fees (revenue), the platform fee account
 * balance, and its money flows (earned vs spent) for the current filters.
 */
const PlatformSummaryCards: React.FC<PlatformSummaryCardsProps> = ({
  summary,
  isLoading,
  moduleKey,
}) => {
  const { toUsdUnits } = usePlatformUsdRates();

  if (isLoading || !summary) return null;

  const account = summary.platform_account;
  const moduleKeys: PlatformModuleKey[] = ['p2p', 'swap', 'withdrawal', 'deposit'];
  const totalFeesUsd = moduleKeys.reduce(
    (sum, key) => sum + (summary.modules[key]?.usd_total ?? 0),
    0
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      <div className="card p-4 border-emerald-500/30">
        <p className="text-xs uppercase tracking-wider text-slate-500">
          Platform fees earned{moduleKey ? ` — ${MODULE_LABELS[moduleKey]}` : ''}
        </p>
        {moduleKey ? (
          <>
            <p className="text-2xl font-bold text-emerald-400 mt-1">
              {formatUsd(summary.modules[moduleKey]?.usd_total ?? 0)}
            </p>
            <div className="mt-2 space-y-1 text-xs font-mono">
              {(summary.modules[moduleKey]?.fees ?? []).map((fee) => (
                <div key={fee.currency} className="flex justify-between gap-2">
                  <span className="text-slate-400">
                    {fee.currency}
                    <span className="text-slate-600"> · {fee.operations} ops</span>
                  </span>
                  <span className="text-right text-emerald-400">
                    +{formatCryptoAmount(fee.amount)}
                    {fee.usd_value != null && (
                      <span className="text-slate-500"> ({formatUsd(fee.usd_value)})</span>
                    )}
                    {fee.transaction_fee !== undefined && fee.hidden_fee !== undefined && (
                      <span className="block text-[10px] text-slate-500">
                        visible {formatCryptoAmount(fee.transaction_fee)}
                        {toUsdUnits(fee.transaction_fee, fee.currency) != null && (
                          <> ({formatUsd(toUsdUnits(fee.transaction_fee, fee.currency))})</>
                        )}{' '}
                        / hidden {formatCryptoAmount(fee.hidden_fee)}
                        {toUsdUnits(fee.hidden_fee, fee.currency) != null && (
                          <> ({formatUsd(toUsdUnits(fee.hidden_fee, fee.currency))})</>
                        )}
                      </span>
                    )}
                  </span>
                </div>
              ))}
              {(summary.modules[moduleKey]?.fees ?? []).length === 0 && (
                <p className="text-slate-500">
                  {moduleKey === 'deposit'
                    ? 'Deposits are free — no platform fees.'
                    : 'No fees collected for the selected filters.'}
                </p>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="text-2xl font-bold text-emerald-400 mt-1">{formatUsd(totalFeesUsd)}</p>
            <div className="mt-2 space-y-1 text-xs font-mono">
              {moduleKeys.map((key) => (
                <div key={key} className="flex justify-between gap-2">
                  <span className="text-slate-400">{MODULE_LABELS[key]}</span>
                  <span className="text-emerald-400">
                    {formatUsd(summary.modules[key]?.usd_total ?? 0)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="card p-4 border-sky-500/30">
        <p className="text-xs uppercase tracking-wider text-slate-500">Platform balance</p>
        {account.configured ? (
          <>
            <p className="text-2xl font-bold text-white mt-1">{formatUsd(account.total_usd)}</p>
            <p className="text-xs text-slate-500 mt-1">
              Fee account <span className="font-mono">#{account.user_id}</span>
            </p>
            <div className="mt-2 space-y-1 text-xs font-mono">
              {account.balances.map((row) => (
                <div key={row.currency} className="flex justify-between gap-2">
                  <span className="text-slate-400">{row.currency}</span>
                  <span className="text-right text-slate-200">
                    {formatCryptoAmount(row.balance)}
                    <span className="text-slate-500"> ({formatUsd(row.usd_value)})</span>
                  </span>
                </div>
              ))}
              {account.balances.length === 0 && (
                <p className="text-slate-500">No balances on the platform fee account yet.</p>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-400 mt-2">
            Platform fee account is not configured (P2P_PLATFORM_FEE_ACCOUNT_USER_ID).
          </p>
        )}
      </div>

      <div className="card p-4 border-amber-500/30">
        <p className="text-xs uppercase tracking-wider text-slate-500">
          Platform account flows
        </p>
        {account.configured ? (
          <>
            <div className="mt-1 flex items-baseline gap-3">
              <p className="text-lg font-bold text-emerald-400">
                +{formatUsd(account.flows.usd_in)}
              </p>
              <p className="text-lg font-bold text-rose-400">
                −{formatUsd(account.flows.usd_out)}
              </p>
            </div>
            <p className="text-xs text-slate-500 mt-1">earned / spent for the selected period</p>
            <div className="mt-2 space-y-1 text-xs font-mono">
              {account.flows.currencies.map((row) => (
                <div key={row.currency} className="flex justify-between gap-2">
                  <span className="text-slate-400">{row.currency}</span>
                  <span className="text-right">
                    <span className="text-emerald-400">+{formatCryptoAmount(row.in)}</span>
                    <span className="text-slate-600"> / </span>
                    <span className="text-rose-400">−{formatCryptoAmount(row.out)}</span>
                  </span>
                </div>
              ))}
              {account.flows.currencies.length === 0 && (
                <p className="text-slate-500">No movements for the selected filters.</p>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-400 mt-2">
            Flows are unavailable until the platform fee account is configured.
          </p>
        )}
      </div>
    </div>
  );
};

export default PlatformSummaryCards;
